import classnames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import client from '../../../utils/api-client';
import { useAutoCompleteField } from '../../hooks/useAutocompleteField';
import useCombinedRefs from '../../hooks/useCombinedRefs';
import useEventCallback from '../../hooks/useEventCallback';
import Checkbox from '../Checkbox';
import AutoComplete from './AutoComplete';
import { Textarea } from '../index';

const lastDatasetByFieldName = {};

function AddressAutoCompeteField({
  name, disabled, city, inputRef, initialValue, compact, ...props
}) {
  const localInputRef = useRef();
  const { t } = useTranslation();
  const { sessionId } = useSelector(state => state.app);
  const lastInputValueRef = useRef(initialValue?.address ?? props.value?.address);
  const cantFindAddressInitial = !initialValue && !props.value ? false : !(initialValue?.fiasLevel ?? props.value?.fiasLevel);
  const [cantFindAddress, setCantFindAddress] = useState(cantFindAddressInitial);
  const getDataset = useEventCallback(search => client({ path: `/internal/suggest/address/${sessionId}`, method: 'GET' }, { q: search, city }).then(
    ({ suggestions }) => {
      const dataset = (localInputRef.current.value === `${suggestions[0]?.address} ` ? suggestions.slice(1) : suggestions)
        .map(
          (item, i) => ({
            id: i.toString(),
            label: `${item.address}${item.fiasLevel && item.fiasLevel < 8 ? ' ' : ''}`,
            value: item,
            disabled: item.city !== city && item.region !== city,
          }),
        );
      lastDatasetByFieldName[name] = dataset;
      return { search: `${city}-${search}`, dataset };
    },
  ));

  const field = useAutoCompleteField(name, getDataset, props, `${city}-`);

  const handleCantFindAddressChange = useEventCallback((value) => {
    lastInputValueRef.current = localInputRef.current.value;
    setCantFindAddress(value);
    props.onChange(
      value
        ? { address: lastInputValueRef.current }
        : (lastDatasetByFieldName[name]?.find(item => item.value.address === lastInputValueRef.current.trim())?.value ?? { address: '' }),
    );
  });

  const handleInputChange = useCallback((value) => {
    props.onChange({ address: value });
  }, []);


  useEffect(() => {
    if (props.value?.fiasLevel && props.value.fiasLevel < 8) {
      setTimeout(() => {
        localInputRef.current.focus();
      }, 200); // TODO risky, why 200 ok, but 150 not???
    }
  }, [props.value]);

  return (
    <>
      {cantFindAddress ? (
        <Textarea
          inputRef={useCombinedRefs(localInputRef, inputRef)}
          className="ui-input-component address-textarea"
          name={props.name}
          value={props.value?.address}
          label={props.label}
          valid={props.valid}
          message={props.message}
          onChange={handleInputChange}
        />
      ) : (
        <AutoComplete
          {...props}
          {...field}
          initialValue={lastInputValueRef.current}
          inputComponent={Textarea}
          inputRef={useCombinedRefs(localInputRef, inputRef)}
          disabled={disabled}
          className={classnames('address-autocomplete', { compact })}
        />
      )}
      {!compact && (
        <Checkbox
          className="address-checkbox"
          value={cantFindAddress}
          label={t('common.cant_find_address')}
          onChange={handleCantFindAddressChange}
        />
      )}
    </>
  );
}

export default AddressAutoCompeteField;
